// Convert a recorded audio Blob (whatever the browser's MediaRecorder produced —
// Chrome gives WebM/Opus) into a real PCM16 WAV Blob.
//
// Why: the speech-to-text backend now transcribes via AWS Bedrock (Voxtral), whose
// server-side decoder is libsndfile — it reads WAV/FLAC/OGG but NOT WebM/Opus, so a
// raw MediaRecorder blob is rejected with "Format not recognised". MediaRecorder
// can't emit WAV natively, so we decode the recording to raw PCM via Web Audio
// (which handles the Opus decode in-browser) and encode the WAV container ourselves.
// Downmixed to mono and resampled to 16 kHz — Voxtral's preferred input and a much
// smaller upload than 48 kHz stereo.

const TARGET_SAMPLE_RATE = 16000

export async function blobToWavBlob(blob, targetSampleRate = TARGET_SAMPLE_RATE) {
  const arrayBuffer = await blob.arrayBuffer()

  const AudioCtx = window.AudioContext || window.webkitAudioContext
  const decodeCtx = new AudioCtx()
  let decoded
  try {
    decoded = await decodeCtx.decodeAudioData(arrayBuffer)
  } finally {
    decodeCtx.close()
  }

  // Downmix to mono + resample to the target rate by rendering through an
  // OfflineAudioContext pinned to that sample rate (high-quality resample for free).
  const frameCount = Math.max(1, Math.ceil(decoded.duration * targetSampleRate))
  const offlineCtx = new OfflineAudioContext(1, frameCount, targetSampleRate)
  const source = offlineCtx.createBufferSource()
  source.buffer = decoded
  source.connect(offlineCtx.destination)
  source.start(0)
  const rendered = await offlineCtx.startRendering()

  return encodeWav(rendered.getChannelData(0), targetSampleRate)
}

// Encode mono float32 samples ([-1, 1]) as a 16-bit PCM WAV Blob.
function encodeWav(samples, sampleRate) {
  const bytesPerSample = 2
  const dataSize = samples.length * bytesPerSample
  const buffer = new ArrayBuffer(44 + dataSize)
  const view = new DataView(buffer)

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i))
  }

  writeString(0, "RIFF")
  view.setUint32(4, 36 + dataSize, true)
  writeString(8, "WAVE")
  writeString(12, "fmt ")
  view.setUint32(16, 16, true) // PCM fmt chunk size
  view.setUint16(20, 1, true) // audio format = PCM
  view.setUint16(22, 1, true) // channels = mono
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * bytesPerSample, true) // byte rate
  view.setUint16(32, bytesPerSample, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  writeString(36, "data")
  view.setUint32(40, dataSize, true)

  let offset = 44
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
    offset += bytesPerSample
  }

  return new Blob([view], { type: "audio/wav" })
}
